import Store from 'store/store';
import { del, post, request } from 'utils/api';

// --- API requests ---

const fetchBaseMovies = () => request('/movies/base/base-movies');
const fetchLocaleMovies = (baseId) => request(`/movies/base/${baseId}`);
const postEnableLocale = (base_movie_id, region_id) => post('/movies/base/locales', { base_movie_id, region_id });
const postRemoveLocale = (base_movie_id, region_id, redirectUrl) => del('/movies/base/locales', { base_movie_id, region_id, redirectUrl });

// --- Cached data access ---

/**
 * Load locale data for a base movie.
 * Returns cached data if available, otherwise fetches from API.
 * @param {string} baseMovieId
 * @param {boolean} forceRefresh - Force a fresh fetch even if cached
 * @returns {Promise<object>} Locale data with `locales` and `notEnabledLocales` arrays
 */
export async function loadMovieLocales(baseMovieId, forceRefresh = false) {
	if (!baseMovieId) return null;

	const cached = Store.get().movieLocales?.[baseMovieId];
	if (cached && !forceRefresh) return cached;

	const data = await fetchLocaleMovies(baseMovieId);
	updateMovieLocalesCache(baseMovieId, data);
	return data;
}

/**
 * Load all base movies available for adding to the current region.
 * Returns cached data if available, otherwise fetches from API.
 * @param {boolean} forceRefresh - Force a fresh fetch even if cached
 * @returns {Promise<object>} Base movies data
 */
export async function loadBaseMovies(forceRefresh = false) {
	const cached = Store.get().baseMovies;
	if (cached && !forceRefresh) return cached;

	const data = await fetchBaseMovies();
	Store.get().set({ baseMovies: data }).now();
	return data;
}

// --- Locale mutations ---

/**
 * Enable a locale for a movie and update the cache.
 * @param {string} baseMovieId
 * @param {string} regionId
 * @returns {Promise<object>} Updated locale data
 */
export async function enableLocaleForMovie(baseMovieId, regionId) {
	const data = await postEnableLocale(baseMovieId, regionId);
	updateMovieLocalesCache(baseMovieId, data);
	return data;
}

/**
 * Remove a locale from a movie and refresh the cache.
 * @param {string} baseMovieId
 * @param {string} regionId
 * @param {string} redirectUrl (Optional) - URL to redirect to after unpublishing (for live locales)
 * @returns {Promise<object>} Updated locale data
 */
export async function removeLocaleForMovie(baseMovieId, regionId, redirectUrl) {
	await postRemoveLocale(baseMovieId, regionId, redirectUrl);
	// Refresh cache after removal and return updated data
	return await loadMovieLocales(baseMovieId, true);
}

// --- Getters for cached data ---

/**
 * Get cached locale data for a base movie.
 * @param {string} baseMovieId
 * @returns {object|null} Cached locale data or null if not available
 */
export function getMovieLocales(baseMovieId) {
	return Store.get().movieLocales?.[baseMovieId] || null;
}

// --- Internal helpers ---

function updateMovieLocalesCache(baseMovieId, data) {
	const existing = Store.get().movieLocales || {};
	Store.get().set({ movieLocales: { ...existing, [baseMovieId]: data } }).now();
	Store.emit('UPDATE_MOVIE_LOCALES', baseMovieId);
}
